import nl2br from 'react-nl2br';
import _ from 'lodash';
import { EntityType, ITicketsInfo, IUser, IBuyButtonEventExtraProps } from '@common/redux/types/common';
import { areTicketsSoldOut, getTicketsInfoPriceStr, isTicketsInfoAvailable } from '@common/utils/tickets';
import { showBuyModal } from "@common/utils/showBuyModal";
import { chars } from '@common/utils/constants';
import { checkCanOpenMuzaWidget, openMuzaWidget } from './muzaWidget/helpers/helpers';

interface IButtonProps {
  price: string;
  text: string;
  hint: string;
  disabled?: boolean;
  empty?: boolean;
  // extraHintStyles - это свойство необходимо для хинтов с длинным текстом,
  // чтобы снизить размер верхнего и нижнего падинга с 17px до 14px
  extraHintStyles?: string | null;
  onClick?: (user: IUser) => void;
  defaultDate?: number;
  eventExtraProps?: IBuyButtonEventExtraProps;
}

export const getHostname = (link: string) => {
  let hostname = '';
  try {
    hostname = new URL(link).hostname;
    // eslint-disable-next-line no-empty
  } catch (e) {
  }

  return hostname;
}

export const hintsHash = {
  registryOnSite: (url: string, isMuzaWidget = false) => isMuzaWidget ?
    'Оформить бесплатный билет в билетной системе «Муза»' :
    `Необходимо зарегистрироваться на сайте ${url}`,
  soldOnSite: (url: string, isMuzaWidget = false) => isMuzaWidget ?
    'Билеты продаются в билетной системе «Муза»' :
    `Билеты продаются на сайте ${url}`,
  registry: () => 'Необходимо зарегистрироваться',
  sold: () => 'Билеты продаются организатором',
  pushkinCard: () => 'Можно оплатить Пушкинской картой',
  ticketsOut: () => 'Билеты закончились',
  goToSite: (url: string) => `Переход на сайт ${url}`,
};

const openLink = (link) => {
  if (checkCanOpenMuzaWidget(link)) {
    openMuzaWidget(link);
  } else {
    window.open(link, '_blank').focus();
  }
};

export const getButtonProps = (
  ticketsInfos: ITicketsInfo[],
  externalSaleLink: string,
  isFree: boolean,
  eipskPrices: { min: number, max: number },
  entityId: number,
  entityType: EntityType, prices: number[],
  defaultDate,
  eventExtraProps: IBuyButtonEventExtraProps = {},
): IButtonProps => {
  const { isBenefitZ, ...restEventExtraProps } = eventExtraProps;

  const openBuyWidget = (user: IUser) => showBuyModal({
    entityType,
    entityId,
    defaultDate,
    user,
    eventExtraProps: {
      ...restEventExtraProps,
      ...(isBenefitZ && { isBenefitZ: true }),
    },
  });

  // Продаются на КР

  const availableTicketsInfos = _(ticketsInfos)
    .filter((ticketsInfo) => isTicketsInfoAvailable(ticketsInfo) || areTicketsSoldOut(ticketsInfo))
    .value();

  const maxPrice = entityType === 'ticketsbundles' ? Math.max(...prices) :
    _(availableTicketsInfos).map('maxPrice').max();
  let minPrice = entityType === 'ticketsbundles' ? Math.min(...prices) : _(availableTicketsInfos).map('minPrice').min();
  const available = _(availableTicketsInfos).map('available').max();
  const total = _(availableTicketsInfos).map('total').max();
  const unlimited = _(availableTicketsInfos).map('unlimited').find(() => true);

  const isAllTicketsSoldOut = entityType === 'ticketsbundles' ? false : _(ticketsInfos)
    .filter((ticketsInfo) => areTicketsSoldOut(ticketsInfo))
    .value()
    .length === ticketsInfos?.length;

  if (isBenefitZ) {
    if (available > 0 || unlimited) {
      return {
        text: 'Zабронировать',
        hint: undefined,
        price: 'для участников СВО и членов их семей',
        disabled: false,
        onClick: openBuyWidget,
      }
    }

    if (isAllTicketsSoldOut || (available === 0 && total > 0 && !unlimited)) {
      return {
        text: 'Билеты Zакончились',
        hint: undefined,
        price: undefined,
        disabled: true,
        onClick: openBuyWidget,
      }
    }

    /** Если ticketsInfoBenefitZ пришел, но внутри нет билетов, то возвращаем empty: true **/
    return {
      text: undefined,
      hint: undefined,
      price: undefined,
      empty: true,
    }
  }

  if (!_.isUndefined(minPrice) && !_.isUndefined(maxPrice)) {
    if ((minPrice === 0 || minPrice > 0) && available === 0 && total > 0 && !unlimited && !externalSaleLink) {
      return {
        text: entityType === 'ticketsbundles' ? 'По абонементу закончились билеты' : 'Билеты закончились',
        hint: 'Подробности у организатора',
        price: undefined,
        disabled: true,
        onClick: openBuyWidget,
      }
    }
    if (maxPrice === minPrice && maxPrice === 0 && (available > 0 || unlimited)) {
      return {
        text: undefined,
        hint: hintsHash.registry(),
        price: 'БЕСПЛАТНО',
        disabled: isAllTicketsSoldOut,
        onClick: openBuyWidget,
      }
    }
    if (available > 0 || unlimited) {
      minPrice = entityType === 'ticketsbundles' ? Math.min(...prices) :
        _(availableTicketsInfos.filter(ticketsInfo => ticketsInfo.available > 0 ||
          ticketsInfo.unlimited)).map('minPrice').min();
      return {
        text: entityType === 'ticketsbundles' ? 'купить абонемент' : 'купить билет',
        price: getTicketsInfoPriceStr({ maxPrice, minPrice }),
        hint: undefined,
        disabled: isAllTicketsSoldOut,
        onClick: openBuyWidget,
      }
    }
  }

  // Если есть ссылка на продажу билетов
  if (externalSaleLink) {
    const saleLink = getHostname(externalSaleLink);

    // Если билеты бесплатны
    if (isFree) {
      return {
        price: 'БЕСПЛАТНО',
        text: undefined,
        hint: hintsHash.registryOnSite(saleLink, checkCanOpenMuzaWidget(externalSaleLink)),
        extraHintStyles: 'registryOnSite',
        onClick: () => openLink(externalSaleLink),
      }
    }
    // Цены пришли из еипска
    if (eipskPrices) {
      if (eipskPrices?.min === eipskPrices?.max && eipskPrices?.max === 0) {
        return {
          text: undefined,
          hint: hintsHash.registryOnSite(saleLink, checkCanOpenMuzaWidget(externalSaleLink)),
          price: 'БЕСПЛАТНО',
          extraHintStyles: 'registryOnSite',
          onClick: () => openLink(externalSaleLink),
        }
      }

      return {
        text: 'купить билет',
        price: getTicketsInfoPriceStr({ maxPrice: eipskPrices?.max * 100, minPrice: eipskPrices?.min * 100 }),
        hint: hintsHash.soldOnSite(saleLink, checkCanOpenMuzaWidget(externalSaleLink)),
        onClick: () => openLink(externalSaleLink),
      }
    }

    // Если данных про билеты нет
    return {
      hint: hintsHash.soldOnSite(saleLink, checkCanOpenMuzaWidget(externalSaleLink)),
      price: undefined,
      text: 'купить билет',
      onClick: () => openLink(externalSaleLink),
    }
  } else { // Ссылки на на продажу нет
    // Билеты бесплатны
    if (isFree) {
      return {
        text: undefined,
        hint: undefined,
        price: 'БЕСПЛАТНО',
        empty: true,
      }
    }

    // Цены пришли из еипска
    if (eipskPrices) {
      if (eipskPrices?.min === eipskPrices?.max && eipskPrices?.max === 0) {
        return {
          text: undefined,
          hint: undefined,
          price: 'БЕСПЛАТНО',
          empty: true,
        }
      }

      return {
        text: undefined,
        price: getTicketsInfoPriceStr({ maxPrice: eipskPrices?.max * 100, minPrice: eipskPrices?.min * 100 }),
        hint: hintsHash.sold(),
        empty: true,
      }
    }

    return {
      text: undefined,
      price: undefined,
      hint: undefined,
      empty: true,
    }
  }
}

const getTextWithBrs = (text: string): string => nl2br(text.split(' ').join('\n')) as any;

export const getButtonPropsForHotelOrRestaurant = (
  isHotel: boolean, isRestaurant: boolean,
  externalSaleLink: string, roomCost: number, averageCheck: number
): IButtonProps => {
  const text = getTextWithBrs(isHotel ? 'посмотреть цены' : isRestaurant && 'забронировать столик');
  if (externalSaleLink) {
    const saleLink = getHostname(externalSaleLink);
    if (isHotel && roomCost || isRestaurant && averageCheck) {
      return {
        hint: hintsHash.goToSite(saleLink),
        text,
        price: isHotel ? `от ${roomCost} ${chars.rouble}` : isRestaurant && `≈ ${averageCheck} ${chars.rouble}`,
        onClick: () => openLink(externalSaleLink),
      }
    }
    return {
      hint: hintsHash.goToSite(saleLink),
      text,
      price: undefined,
      onClick: () => window.open(externalSaleLink, '_blank').focus(),
    }
  } else {
    if (isHotel && roomCost || isRestaurant && averageCheck) {
      return {
        hint: undefined,
        text,
        price: isHotel ? `от ${roomCost} ${chars.rouble}` : isRestaurant && `≈ ${averageCheck} ${chars.rouble}`,
        disabled: true
      }
    }
    return {
      hint: undefined,
      text,
      price: undefined,
      disabled: true,
    }
  }
}
